import type { SplineElementType } from '@elements/types';
import type { AttributeState } from '@projects/edit-modes/Attributes';
import { EditMode } from '@projects/edit-modes/types';
import { DepthColors, SegmentColors, TrenchOpacity, WidthColors } from '@trenches/colors';
import type {
  AttributeType,
  Depth,
  LayingType,
  RoadContext,
  SurfaceType,
  Width,
} from '@trenches/types';

const getAttributeColor = (
  selectedAttributeType: AttributeType,
  layingType: LayingType | null,
  surfaceType: SurfaceType | null,
  depth: Depth | null,
  width: Width | null,
  roadContext: RoadContext | null,
  attributeState: AttributeState,
): string => {
  if (selectedAttributeType === 'LAYING_TYPE' && layingType) return SegmentColors[layingType];
  if (selectedAttributeType === 'SURFACE_TYPE' && surfaceType) return SegmentColors[surfaceType];
  if (selectedAttributeType === 'ROAD_CONTEXT' && roadContext) return SegmentColors[roadContext];
  if (selectedAttributeType === 'DEPTH' && depth) {
    if (attributeState.showOnlyCurrentDepth && attributeState.depth !== depth)
      return SegmentColors.Default;
    return DepthColors[depth];
  }
  if (selectedAttributeType === 'WIDTH' && width) {
    if (attributeState.showOnlyCurrentWidth && attributeState.width !== width)
      return SegmentColors.Default;
    return WidthColors[width];
  }
  return SegmentColors.Default;
};

export const getTrenchSegmentColor = (
  mode: EditMode,
  isMouseOverTrench: boolean,
  isSelected: boolean,
  isSelectedElementInSegment: boolean,
  containsElements: boolean,
  selectedAttributeType: AttributeType,
  layingType: LayingType | null,
  surfaceType: SurfaceType | null,
  depth: Depth | null,
  width: Width | null,
  roadContext: RoadContext | null,
  attributeState: AttributeState,
  elementType?: SplineElementType,
): string => {
  if (mode === EditMode.TrenchAttributes)
    return getAttributeColor(
      selectedAttributeType,
      layingType,
      surfaceType,
      depth,
      width,
      roadContext,
      attributeState,
    );
  if (isSelectedElementInSegment && elementType) return elementType.color;
  if (mode === EditMode.Element)
    return containsElements ? SegmentColors.WithElement : SegmentColors.MissingElement;
  if (isSelected) return SegmentColors.Selected;
  if (isMouseOverTrench) return SegmentColors.Hover;
  if (layingType === 'SOIL_DISPLACEMENT') return SegmentColors.SOIL_DISPLACEMENT;
  if (mode === EditMode.Trench) return SegmentColors.TrenchMode;
  if (mode === EditMode.Scan) return SegmentColors.ScanMode;
  return SegmentColors.Default;
};

export const getTrenchSegmentOpacity = (
  mode: EditMode,
  isTrenchSelected: boolean,
  color: string,
): number => {
  if (mode === EditMode.TrenchAttributes)
    return color === SegmentColors.Default ? TrenchOpacity.Light : TrenchOpacity.Default;
  if (mode === EditMode.Element) return TrenchOpacity.Default;
  return isTrenchSelected ? TrenchOpacity.Selected : TrenchOpacity.Default;
};
